/*  AdminReferrals — Unified Referrals & Leads hub (iter100.203).
 *
 *  Replaces the old two-tab split (Demo Links + Referrals + Activity
 *  feed) with a single CRM-style lead list. Every doctor / sales /
 *  admin-created demo link produces one or more lead rows:
 *    • Unclaimed link           → 1 lead (stage = sent | expired | revoked)
 *    • Link with N claims       → N leads (stage = engaged | converted)
 *
 *  Rewards are an attribute of the lead — reward status / amount /
 *  badge live inline on the row. Approve/reject happens via a dialog
 *  triggered from the row itself. No separate reward tab.
 *
 *  Assignment + lead status (open / contacted / won / lost) also live
 *  on the row, mutating via the existing
 *  /api/admin/demo-links/claims/{token}/{claim_index}/(assign|status)
 *  endpoints (kept from the previous iteration — that's where the
 *  assignment data actually persists).
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Gift, HourglassMedium, CheckCircle, XCircle, Coins, Trophy, Sparkle,
    PaperPlaneTilt, UserCheck, Broadcast, TrendUp, Warning, Clock,
    WhatsappLogo, EnvelopeSimple, UserFocus, DotsThreeVertical,
    Prohibit, CaretDown,
} from "@phosphor-icons/react";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";


const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function relTime(iso) {
    if (!iso) return "—";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "—";
    const diff = Date.now() - t;
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
}


const STAGE_META = {
    sent:      { Icon: PaperPlaneTilt, cls: "bg-sky-100 text-sky-800 ring-sky-200",           label: "Sent" },
    engaged:   { Icon: UserCheck,      cls: "bg-amber-100 text-amber-800 ring-amber-200",     label: "Engaged" },
    converted: { Icon: Trophy,         cls: "bg-emerald-100 text-emerald-800 ring-emerald-200", label: "Converted" },
    expired:   { Icon: HourglassMedium, cls: "bg-slate-100 text-slate-700 ring-slate-200",    label: "Expired" },
    revoked:   { Icon: Prohibit,       cls: "bg-rose-100 text-rose-700 ring-rose-200",        label: "Revoked" },
};

const SOURCE_META = {
    doctor_referral: { label: "Doctor referral", cls: "bg-primary/10 text-primary" },
    sales_outreach:  { label: "Sales outreach",  cls: "bg-violet-100 text-violet-800" },
    admin:           { label: "Admin",           cls: "bg-slate-100 text-slate-700" },
    other:           { label: "Other",           cls: "bg-secondary text-muted-foreground" },
};

const LEAD_STATUS_META = {
    open:      { cls: "bg-sky-100 text-sky-800 ring-sky-200",             label: "Open" },
    contacted: { cls: "bg-amber-100 text-amber-800 ring-amber-200",       label: "Contacted" },
    won:       { cls: "bg-emerald-100 text-emerald-800 ring-emerald-200", label: "Won" },
    lost:      { cls: "bg-slate-100 text-slate-700 ring-slate-200",       label: "Lost" },
};

const REWARD_STATUS_META = {
    pending:  { cls: "bg-amber-50 text-amber-800 ring-amber-200",       label: "Pending review", Icon: HourglassMedium },
    approved: { cls: "bg-emerald-50 text-emerald-800 ring-emerald-200", label: "Approved",       Icon: CheckCircle },
    rejected: { cls: "bg-slate-100 text-slate-700 ring-slate-200",      label: "Rejected",       Icon: XCircle },
};


export default function AdminReferrals() {
    const [leads, setLeads] = useState([]);
    const [kpis, setKpis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        stage: "all", source: "all", assigned_to: "all",
        reward_status: "any", days: 90, q: "",
    });
    const [salesUsers, setSalesUsers] = useState([]);
    const [approving, setApproving] = useState(null);
    const [rejecting, setRejecting] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const p = new URLSearchParams({
                stage: filters.stage, source: filters.source,
                assigned_to: filters.assigned_to, reward_status: filters.reward_status,
                days: String(filters.days),
            });
            if (filters.q.trim()) p.set("q", filters.q.trim());
            const [leadsResp, kpisResp] = await Promise.all([
                api.get(`/admin/referrals/leads?${p.toString()}`),
                api.get(`/admin/referrals/kpis?days=${filters.days}`),
            ]);
            setLeads(leadsResp.data.leads || []);
            setKpis(kpisResp.data || null);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load referrals");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { reload(); }, [reload]);

    useEffect(() => {
        api.get("/admin/sales-users").then((r) => setSalesUsers(r.data || [])).catch(() => {});
    }, []);

    return (
        <div className="space-y-6" data-testid="admin-referrals">
            {/* HEADER — iter101.31 · Universal admin page grammar. */}
            <div>
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Work · Referrals</div>
                <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Referrals</h1>
                <p className="text-xs text-muted-foreground mt-1">
                    Doctor referrals, sales outreach, and admin-generated demo links — all flow into this single CRM.
                </p>
            </div>

            {/* KPI STRIP — 5 hero tiles per user spec:
                Total · Pending · Accepted (converted) · Rejected (rewards
                rejected) · Rewarded (paid). Each tile drills into its
                bucket by pre-setting the stage/reward filter. */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="admin-referrals-kpis">
                <RefTile
                    testid="admin-referrals-tile-total"
                    label="Total leads"
                    value={kpis?.total_leads ?? 0}
                    Icon={Broadcast} tone="primary"
                    hint={`${kpis?.days ?? filters.days}d window`}
                    onClick={() => setFilters((f) => ({ ...f, stage: "all", reward_status: "any" }))}
                    active={filters.stage === "all" && filters.reward_status === "any"}
                />
                <RefTile
                    testid="admin-referrals-tile-pending"
                    label="Pending"
                    value={kpis?.pending_referrals ?? 0}
                    Icon={Clock} tone="amber"
                    hint="Awaiting review"
                    onClick={() => setFilters((f) => ({ ...f, reward_status: "pending", stage: "all" }))}
                    active={filters.reward_status === "pending"}
                />
                <RefTile
                    testid="admin-referrals-tile-accepted"
                    label="Accepted"
                    value={kpis?.converted ?? 0}
                    Icon={CheckCircle} tone="emerald"
                    hint="Real signups"
                    onClick={() => setFilters((f) => ({ ...f, stage: "converted", reward_status: "any" }))}
                    active={filters.stage === "converted"}
                />
                <RefTile
                    testid="admin-referrals-tile-rejected"
                    label="Rejected"
                    value={kpis?.rewards_rejected ?? 0}
                    Icon={XCircle} tone="rose"
                    hint="Rewards declined"
                    onClick={() => setFilters((f) => ({ ...f, reward_status: "rejected", stage: "all" }))}
                    active={filters.reward_status === "rejected"}
                />
                <RefTile
                    testid="admin-referrals-tile-rewarded"
                    label="Rewarded"
                    value={fmtINR(kpis?.rewards_paid_inr || 0)}
                    Icon={Trophy} tone="violet"
                    hint={`${kpis?.days ?? filters.days}d paid`}
                    onClick={() => setFilters((f) => ({ ...f, reward_status: "approved", stage: "all" }))}
                    active={filters.reward_status === "approved"}
                />
            </div>

            {/* FILTER STRIP — Universal grammar (search + dropdowns +
                bucket chips). Stage chips are the primary filter axis;
                Source / Assigned / Range collapse into dropdowns so the
                surface stays as clean as /admin/patients. */}
            <div className="flex flex-col gap-3" data-testid="admin-referrals-filter-bar">
                <FilterSearchBar
                    query={filters.q || ""}
                    setQuery={(v) => setFilters((f) => ({ ...f, q: v }))}
                    placeholder="Search doctor / visitor / email / token…"
                    searchTestid="admin-referrals-search"
                    testid="admin-referrals-filter-search"
                    rightSlot={(
                        <>
                            <RefDropdown value={filters.source} onChange={(v) => setFilters((f) => ({ ...f, source: v }))}
                                testid="admin-referrals-source-select"
                                options={[
                                    { k: "all", label: "All sources" },
                                    { k: "doctor_referral", label: "Doctor" },
                                    { k: "sales_outreach", label: "Sales" },
                                    { k: "admin", label: "Admin" },
                                ]} />
                            <RefDropdown value={filters.assigned_to} onChange={(v) => setFilters((f) => ({ ...f, assigned_to: v }))}
                                testid="admin-referrals-assigned-select"
                                options={[
                                    { k: "all", label: "Anyone" },
                                    { k: "unassigned", label: "Unassigned" },
                                    ...salesUsers.slice(0, 6).map((u) => ({ k: u.id, label: (u.name || "User").split(" ")[0] })),
                                ]} />
                            <RefDropdown value={String(filters.days)} onChange={(v) => setFilters((f) => ({ ...f, days: Number(v) }))}
                                testid="admin-referrals-range-select"
                                options={[
                                    { k: "7", label: "7d" },
                                    { k: "30", label: "30d" },
                                    { k: "90", label: "90d" },
                                    { k: "365", label: "1y" },
                                ]} />
                        </>
                    )}
                />
                <FilterChipRow
                    chips={[
                        { id: "all",         label: "All stages" },
                        { id: "sent",        label: "Sent",        tone: "primary" },
                        { id: "engaged",     label: "Engaged",     tone: "amber" },
                        { id: "converted",   label: "Converted",   tone: "emerald" },
                        { id: "expired",     label: "Expired",     tone: "slate" },
                        { id: "revoked",     label: "Revoked",     tone: "rose" },
                    ]}
                    value={filters.stage}
                    onChange={(v) => setFilters((f) => ({ ...f, stage: v }))}
                    testid="admin-referrals-chip-row"
                    chipTestidPrefix="admin-referrals-pill"
                />
            </div>

            {/* Leads list */}
            {loading && <div className="text-center text-sm text-muted-foreground py-10">Loading leads…</div>}
            {!loading && leads.length === 0 && (
                <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center" data-testid="admin-referrals-empty">
                    <Broadcast size={28} weight="duotone" className="mx-auto text-muted-foreground/60" />
                    <div className="mt-2 text-sm font-medium">No leads match your filters</div>
                    <div className="text-xs text-muted-foreground mt-1">Try widening the date range or clearing filters.</div>
                </div>
            )}
            {!loading && leads.length > 0 && (
                <ul className="space-y-2.5" data-testid="admin-referrals-list">
                    {leads.map((lead) => (
                        <LeadRow
                            key={lead.id}
                            lead={lead}
                            salesUsers={salesUsers}
                            onChange={reload}
                            onApproveReward={() => setApproving(lead)}
                            onRejectReward={() => setRejecting(lead)}
                        />
                    ))}
                </ul>
            )}

            <RewardApproveDialog
                open={!!approving}
                lead={approving}
                onClose={() => setApproving(null)}
                onDone={() => { setApproving(null); reload(); }}
            />
            <RewardRejectDialog
                open={!!rejecting}
                lead={rejecting}
                onClose={() => setRejecting(null)}
                onDone={() => { setRejecting(null); reload(); }}
            />
        </div>
    );
}


/* iter101.31 — KPI stat tile (mirrors /admin/patients + /admin/cases). */
function RefTile({ testid, label, value, Icon, tone, hint, onClick, active }) {
    const accent = tone === "emerald" ? "text-emerald-700 bg-emerald-100"
        : tone === "violet" ? "text-violet-700 bg-violet-100"
        : tone === "amber" ? "text-amber-700 bg-amber-100"
        : tone === "rose" ? "text-rose-700 bg-rose-100"
        : "text-primary bg-primary/10";
    const Component = onClick ? "button" : "div";
    return (
        <Component
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`flex items-start gap-3 rounded-xl bg-card border p-3 text-left transition ${
                active ? "border-primary ring-1 ring-primary/30 bg-primary/5"
                       : (onClick ? "border-border hover:border-primary/40 hover:shadow-sm cursor-pointer" : "border-border")
            }`}
            data-testid={testid}
        >
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
                <div className="text-xl font-display font-bold tabular-nums leading-none mt-1">{value}</div>
                {hint && <div className="text-[10px] text-muted-foreground mt-1 truncate">{hint}</div>}
            </div>
        </Component>
    );
}


/* iter101.31 — Compact dropdown for the FilterSearchBar right slot. */
function RefDropdown({ value, onChange, options, testid }) {
    return (
        <div className="relative">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer"
                data-testid={testid}
            >
                {options.map((o) => (
                    <option key={o.k} value={o.k}>{o.label}</option>
                ))}
            </select>
            <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
        </div>
    );
}


function LeadRow({ lead, salesUsers, onChange, onApproveReward, onRejectReward }) {
    const stageMeta = STAGE_META[lead.stage] || STAGE_META.sent;
    const sourceMeta = SOURCE_META[lead.source] || SOURCE_META.other;
    const StageIcon = stageMeta.Icon;
    const hasVisitor = !!lead.visitor;
    const isDoctorReferral = lead.source === "doctor_referral";
    const rewardEligible = hasVisitor && isDoctorReferral;
    const [menuOpen, setMenuOpen] = useState(false);
    const [revokeConfirmOpen, setRevokeConfirmOpen] = useState(false);

    const revokeLink = async () => {
        try {
            await api.post(`/admin/sandbox-links/${lead.token}/revoke-admin`);
            toast.success("Link revoked");
            onChange();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Revoke failed");
        }
        setRevokeConfirmOpen(false);
        setMenuOpen(false);
    };

    return (
        <li
            className="rounded-2xl border border-border bg-card p-4 sm:p-5 hover:border-primary/30 transition-colors"
            data-testid={`admin-referral-lead-${lead.id}`}
        >
            {/* Top: stage + source + link meta */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ring-1 ${stageMeta.cls}`}>
                        <StageIcon size={11} weight="fill" />
                        {stageMeta.label}
                    </span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${sourceMeta.cls}`}>
                        {sourceMeta.label}
                    </span>
                    <span className="font-mono text-[10.5px] text-muted-foreground">{lead.token}</span>
                    <span className="text-[10.5px] text-muted-foreground">· created {relTime(lead.created_at)}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 relative">
                    {hasVisitor && lead.visitor.phone && (
                        <a
                            href={`https://wa.me/${(lead.visitor.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Hi ${(lead.visitor.name || "").split(" ")[0] || ""}, following up on your Invisique sandbox visit.`)}`}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-[11px] font-semibold"
                            data-testid={`lead-wa-${lead.id}`}
                        >
                            <WhatsappLogo size={11} weight="fill" /> WA
                        </a>
                    )}
                    {hasVisitor && lead.visitor.email && (
                        <a
                            href={`mailto:${lead.visitor.email}?subject=${encodeURIComponent("Following up on your Invisique sandbox visit")}`}
                            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-border hover:bg-secondary text-[11px] font-semibold"
                            data-testid={`lead-email-${lead.id}`}
                        >
                            <EnvelopeSimple size={11} weight="bold" /> Email
                        </a>
                    )}
                    {!lead.revoked_at && (
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setMenuOpen((v) => !v)}
                                className="inline-flex items-center h-7 w-7 rounded-full border border-border hover:bg-secondary"
                                data-testid={`lead-menu-btn-${lead.id}`}
                                title="More"
                            >
                                <DotsThreeVertical size={14} weight="bold" />
                            </button>
                            {menuOpen && (
                                <div className="absolute right-0 mt-1 z-20 w-40 rounded-xl border border-border bg-card shadow-lg p-1" data-testid={`lead-menu-${lead.id}`}>
                                    <button
                                        onClick={() => { setMenuOpen(false); setRevokeConfirmOpen(true); }}
                                        className="w-full text-left px-2.5 py-1.5 rounded-md text-[11.5px] font-semibold text-rose-700 hover:bg-rose-50"
                                        data-testid={`lead-menu-revoke-${lead.id}`}
                                    >
                                        Revoke link
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Revoke confirmation dialog (shadcn AlertDialog for design consistency) */}
            <AlertDialog open={revokeConfirmOpen} onOpenChange={setRevokeConfirmOpen}>
                <AlertDialogContent data-testid={`lead-revoke-confirm-${lead.id}`}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Revoke this demo link?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Anyone who already claimed it keeps their sandbox access. New visitors will see a &ldquo;no longer valid&rdquo; screen. This can&apos;t be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`lead-revoke-cancel-${lead.id}`}>Keep active</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={revokeLink}
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                            data-testid={`lead-revoke-confirm-btn-${lead.id}`}
                        >
                            Revoke link
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Body: referrer + visitor + note */}
            <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Referred by</div>
                    <div className="text-[13px] font-semibold truncate mt-0.5">{lead.creator_name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{lead.creator_role}</div>
                </div>
                <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Visitor</div>
                    {hasVisitor ? (
                        <>
                            <div className="text-[13px] font-semibold truncate mt-0.5">{lead.visitor.name || "—"}</div>
                            <div className="text-[11px] text-muted-foreground truncate">
                                {lead.visitor.email || "—"}
                                {lead.visitor.phone ? <> · {lead.visitor.phone}</> : null}
                            </div>
                            {lead.visitor.converted_at && (
                                <div className="text-[10.5px] text-emerald-700 font-semibold mt-0.5">
                                    Converted {relTime(lead.visitor.converted_at)}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-[11.5px] text-muted-foreground italic mt-0.5">
                            {lead.stage === "expired" ? "Link expired without a claim" :
                             lead.stage === "revoked" ? "Link revoked before anyone claimed" :
                             "Not claimed yet"}
                        </div>
                    )}
                </div>
            </div>

            {lead.note && (
                <div className="mt-2 text-[11.5px] text-muted-foreground italic border-l-2 border-border pl-2.5">
                    Doctor&apos;s note: &ldquo;{lead.note}&rdquo;
                </div>
            )}

            {/* Action row: assign, lead status, reward */}
            {hasVisitor && (
                <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 flex-wrap">
                    <AssignControl lead={lead} salesUsers={salesUsers} onChange={onChange} />
                    <LeadStatusControl lead={lead} onChange={onChange} />
                    <div className="ml-auto">
                        <RewardBadge
                            lead={lead}
                            rewardEligible={rewardEligible}
                            onApprove={onApproveReward}
                            onReject={onRejectReward}
                        />
                    </div>
                </div>
            )}
        </li>
    );
}


function AssignControl({ lead, salesUsers, onChange }) {
    const [open, setOpen] = useState(false);
    const assign = async (userId, userName) => {
        try {
            await api.post(`/admin/demo-links/claims/${lead.token}/${lead.claim_index}/assign`, {
                assigned_to_id: userId, notes: "",
            });
            toast.success(userId ? `Assigned to ${userName}` : "Unassigned");
            onChange();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Assign failed");
        }
        setOpen(false);
    };
    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full border text-[11px] font-semibold ${lead.assigned_to_id
                    ? "border-primary/50 bg-primary/10 text-primary"
                    : "border-dashed border-border hover:bg-secondary"}`}
                data-testid={`lead-assign-btn-${lead.id}`}
            >
                <UserFocus size={11} weight="bold" />
                {lead.assigned_to_name ? lead.assigned_to_name.split(" ")[0] : "Assign"}
            </button>
            {open && (
                <div className="absolute left-0 mt-1 z-20 w-56 rounded-xl border border-border bg-card shadow-lg p-1 max-h-64 overflow-y-auto" data-testid={`lead-assign-menu-${lead.id}`}>
                    {lead.assigned_to_id && (
                        <button
                            onClick={() => assign(null, null)}
                            className="w-full text-left px-2.5 py-1.5 rounded-md text-[11px] font-semibold text-rose-700 hover:bg-rose-50"
                            data-testid={`lead-assign-unassign-${lead.id}`}
                        >
                            Unassign
                        </button>
                    )}
                    {salesUsers.map((u) => (
                        <button
                            key={u.id}
                            onClick={() => assign(u.id, u.name)}
                            className="w-full text-left px-2.5 py-1.5 rounded-md text-[11.5px] hover:bg-secondary flex items-center justify-between"
                            data-testid={`lead-assign-user-${u.id}-${lead.id}`}
                        >
                            <span className="truncate">{u.name || "User"}</span>
                            <span className="text-[9.5px] text-muted-foreground uppercase tracking-wider ml-2">
                                {u.staff_type || u.role}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}


function LeadStatusControl({ lead, onChange }) {
    const [open, setOpen] = useState(false);
    const cur = lead.lead_status || "open";
    const meta = LEAD_STATUS_META[cur] || LEAD_STATUS_META.open;
    const set = async (nextStatus) => {
        try {
            await api.post(`/admin/demo-links/claims/${lead.token}/${lead.claim_index}/status`, {
                status: nextStatus, note: "",
            });
            toast.success(`Marked ${nextStatus}`);
            onChange();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Update failed");
        }
        setOpen(false);
    };
    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-wider ring-1 ${meta.cls}`}
                data-testid={`lead-status-btn-${lead.id}`}
            >
                {meta.label}
            </button>
            {open && (
                <div className="absolute left-0 mt-1 z-20 w-40 rounded-xl border border-border bg-card shadow-lg p-1" data-testid={`lead-status-menu-${lead.id}`}>
                    {Object.entries(LEAD_STATUS_META).map(([k, m]) => (
                        <button
                            key={k}
                            onClick={() => set(k)}
                            className={`w-full text-left px-2.5 py-1.5 rounded-md text-[11px] font-semibold hover:bg-secondary ${cur === k ? "text-primary" : ""}`}
                            data-testid={`lead-status-option-${k}-${lead.id}`}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}


function RewardBadge({ lead, rewardEligible, onApprove, onReject }) {
    if (!rewardEligible) {
        return (
            <span className="text-[11px] text-muted-foreground italic" data-testid={`lead-reward-none-${lead.id}`}>
                No reward (not a doctor referral)
            </span>
        );
    }
    const reward = lead.reward;
    if (!reward) {
        // A doctor-referral claim that hasn't produced a referral row yet.
        // Shouldn't happen post-iter100.200 hook, but be defensive.
        return (
            <span className="text-[11px] text-muted-foreground italic" data-testid={`lead-reward-missing-${lead.id}`}>
                Reward not yet queued
            </span>
        );
    }
    const meta = REWARD_STATUS_META[reward.status] || REWARD_STATUS_META.pending;
    const StatusIcon = meta.Icon;
    if (reward.status === "pending") {
        return (
            <div className="flex items-center gap-1.5" data-testid={`lead-reward-pending-${lead.id}`}>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ring-1 ${meta.cls}`}>
                    <StatusIcon size={10} weight="fill" />
                    {meta.label}
                </span>
                <Button
                    size="sm" variant="outline"
                    onClick={onReject}
                    className="h-7 px-2 text-[10.5px] text-rose-700 border-rose-200 hover:bg-rose-50"
                    data-testid={`lead-reward-reject-${lead.id}`}
                >
                    Reject
                </Button>
                <Button
                    size="sm"
                    onClick={onApprove}
                    className="h-7 px-2 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`lead-reward-approve-${lead.id}`}
                >
                    <Sparkle size={10} className="mr-1" weight="fill" /> Approve
                </Button>
            </div>
        );
    }
    if (reward.status === "approved") {
        return (
            <div className="text-[11.5px] text-emerald-800 flex items-center gap-1.5" data-testid={`lead-reward-approved-${lead.id}`}>
                <CheckCircle size={11} weight="fill" />
                <strong>
                    {reward.reward_amount_inr ? fmtINR(reward.reward_amount_inr) : ""}
                    {reward.reward_amount_inr && reward.reward_badge_key ? " + " : ""}
                    {reward.reward_badge_key ? reward.reward_badge_key.replace(/_/g, " ") : ""}
                </strong>
                <span className="text-emerald-700/70">· approved</span>
            </div>
        );
    }
    if (reward.status === "rejected") {
        return (
            <div className="text-[11.5px] text-slate-600 flex items-center gap-1.5" data-testid={`lead-reward-rejected-${lead.id}`}>
                <XCircle size={11} weight="fill" />
                <span className="italic">Rejected: &ldquo;{reward.rejection_reason || "no reason"}&rdquo;</span>
            </div>
        );
    }
    return null;
}


/* ─── Reward dialogs (approve / reject) ─────────────────────────── */

function RewardApproveDialog({ open, lead, onClose, onDone }) {
    const [rewardType, setRewardType] = useState("cash_credit");
    const [amount, setAmount] = useState("");
    const [badgeKey, setBadgeKey] = useState("growth_partner");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open || !lead || !lead.reward) return;
        setRewardType("cash_credit");
        setAmount(String(lead.reward.reward_amount_hint_inr || (lead.reward.trigger === "conversion" ? 2500 : 250)));
        // iter100.204 — Default to "Growth Partner" — the badge purpose-
        // built for doctors who refer other orthodontists. Admins can
        // still pick Ambassador or Mentor Provider if the context fits.
        setBadgeKey("growth_partner");
        setNotes("");
    }, [open, lead]);

    if (!lead || !lead.reward) return null;
    const wantsCash = rewardType === "cash_credit" || rewardType === "both";
    const wantsBadge = rewardType === "badge" || rewardType === "both";

    const submit = async () => {
        if (wantsCash && (!amount || Number(amount) <= 0)) {
            toast.error("Enter a positive cash amount");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/admin/referrals/${lead.reward.referral_id}/approve`, {
                reward_type: rewardType,
                reward_amount_inr: wantsCash ? Number(amount) : null,
                reward_badge_key: wantsBadge ? badgeKey : null,
                notes,
            });
            toast.success("Referral approved");
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Approve failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="lead-reward-approve-dialog">
                <DialogHeader>
                    <DialogTitle>Approve referral reward</DialogTitle>
                    <DialogDescription>
                        Credits <strong>{lead.creator_name}</strong> for referring{" "}
                        <strong>{lead.visitor?.name || "the visitor"}</strong>{" "}
                        ({lead.reward.trigger === "conversion" ? "real conversion" : "sandbox claim"}).
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-1">Reward type</label>
                        <div className="grid grid-cols-4 gap-1.5">
                            {[
                                { k: "cash_credit", label: "Cash", Icon: Coins },
                                { k: "badge", label: "Badge", Icon: Trophy },
                                { k: "both", label: "Both", Icon: Gift },
                                { k: "none", label: "None", Icon: XCircle },
                            ].map((o) => (
                                <button
                                    key={o.k}
                                    type="button"
                                    onClick={() => setRewardType(o.k)}
                                    className={`px-2 py-2 rounded-lg border text-[11px] font-semibold flex flex-col items-center gap-1 transition-colors ${rewardType === o.k
                                        ? "border-primary bg-primary/10 text-primary"
                                        : "border-border hover:bg-secondary"}`}
                                    data-testid={`lead-reward-type-${o.k}`}
                                >
                                    <o.Icon size={14} weight="duotone" />
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {wantsCash && (
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-1">Cash credit (₹)</label>
                            <Input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 2500" data-testid="lead-reward-amount" />
                            <div className="text-[10.5px] text-muted-foreground mt-1">
                                Suggested {lead.reward.trigger === "conversion" ? "conversion" : "claim"} amount pre-filled — override freely.
                            </div>
                        </div>
                    )}
                    {wantsBadge && (
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-1">Badge</label>
                            <div className="grid grid-cols-3 gap-1.5">
                                {[
                                    { key: "growth_partner",       label: "Growth Partner" },
                                    { key: "invisique_ambassador", label: "Ambassador" },
                                    { key: "mentor_provider",      label: "Mentor" },
                                ].map((b) => (
                                    <button
                                        key={b.key} type="button"
                                        onClick={() => setBadgeKey(b.key)}
                                        className={`px-2.5 py-2 rounded-lg border text-[11px] font-semibold text-left transition-colors ${badgeKey === b.key
                                            ? "border-violet-400 bg-violet-50 text-violet-800"
                                            : "border-border hover:bg-secondary"}`}
                                        data-testid={`lead-reward-badge-${b.key}`}
                                    >
                                        {b.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-1">Notes (optional)</label>
                        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. First conversion this quarter — Ambassador unlocked." maxLength={500} rows={2} data-testid="lead-reward-notes" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
                    <Button onClick={submit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="lead-reward-approve-submit">
                        {submitting ? "Approving…" : "Approve"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function RewardRejectDialog({ open, lead, onClose, onDone }) {
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    useEffect(() => { if (open) setReason(""); }, [open]);

    if (!lead || !lead.reward) return null;
    const submit = async () => {
        if (!reason.trim()) { toast.error("Please enter a rejection reason"); return; }
        setSubmitting(true);
        try {
            await api.post(`/admin/referrals/${lead.reward.referral_id}/reject`, { reason: reason.trim() });
            toast.success("Referral rejected");
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reject failed");
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="lead-reward-reject-dialog">
                <DialogHeader>
                    <DialogTitle>Reject referral reward</DialogTitle>
                    <DialogDescription>The doctor will see this reason in their Rewards tile.</DialogDescription>
                </DialogHeader>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Same visitor already awarded for the claim event." maxLength={300} rows={3} data-testid="lead-reward-reject-reason" />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
                    <Button variant="destructive" onClick={submit} disabled={submitting} data-testid="lead-reward-reject-submit">
                        {submitting ? "Rejecting…" : "Reject"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
